import { action, computed, observable, reaction, runInAction } from 'mobx';
import type RulesImportVisualStore from './RulesImportVisualStore';
import TestProjectDataStore from '../../test_projects/stores/TestProjectDataStore';
import type TestProjectsVisualStore from '../../test_projects/stores/TestProjectsVisualStore';
import type TestProjectInputEditorStore from '../../test_projects/stores/TestProjectInputEditorStore';
import type { TestProjectService } from '../../test_projects/services';
import type { PendingRuleImportAttempt } from '../types';
import type { TestProject, TestProjectInputsChanged } from '../../test_projects/types';

type TestProjectReviewContext = {
    attempt: PendingRuleImportAttempt;
    dataStore: TestProjectDataStore;
    project: TestProject;
};

// Owns Test Project review and editor navigation for one import. The import attempt owns only import data.
export default class RulesImportResolutionStore {
    @observable.ref
    dataStore: TestProjectDataStore | undefined;

    private readonly stopObservingImport;

    constructor(
        readonly importStore: RulesImportVisualStore,
        private readonly testProjectService: TestProjectService,
        private readonly projectsStore: TestProjectsVisualStore,
        readonly editorStore: TestProjectInputEditorStore
    ) {
        this.stopObservingImport = reaction(
            () => [importStore.pendingAttempt, importStore.isResolvingTestProjectUsage],
            () => {
                this.dataStore = undefined;
            }
        );
    }

    dispose() {
        this.stopObservingImport();
    }

    async handleInputsChanged(change: TestProjectInputsChanged) {
        const attempt = this.importStore.pendingAttempt;

        if (attempt?.projectId === change.projectId) {
            await this.importStore.refreshBlockers(attempt);
        }
    }

    @action.bound
    async cancelDialog() {
        if (this.isInitialWarning) {
            this.importStore.cancel();
            return;
        }

        const attempt = this.importStore.pendingAttempt;
        const dataStore = this.dataStore;

        if (!attempt || !this.importStore.isResolvingTestProjectUsage) {
            return;
        }

        // Returning still requires a settled blocker read, even for a direct caller.
        if (!this.canReturnFromReview) {
            return;
        }

        // Fresh usage determines whether to show remaining blockers or restore the import screen.
        const loaded = await this.importStore.refreshBlockers(attempt);

        if (!loaded) {
            return;
        }

        runInAction(() => {
            // A late return request must not dismiss a newer import or project review.
            if (
                this.importStore.pendingAttempt !== attempt ||
                this.dataStore !== dataStore ||
                !this.importStore.isResolvingTestProjectUsage ||
                this.importStore.usageStatus !== 'ready'
            ) {
                return;
            }

            // Clearing the project review exposes the blocker overview while resolution stays active.
            this.dataStore = undefined;

            this.importStore.resumeImport();
        });
    }

    @computed
    get isInitialWarning() {
        return !this.dataStore && this.importStore.affectedTestProjects.length > 0;
    }

    @computed
    get canCancelDialog() {
        if (!this.importStore.isResolvingTestProjectUsage) {
            return true;
        }

        // Cancelling an editor returns to its review, so it can stay available during blocker reads.
        if (this.editorStore.getDraftFor(this.dataStore)) {
            return true;
        }

        // Closing the initial warning is always allowed. Returning from review waits for any blocker read.
        return this.isInitialWarning || this.canReturnFromReview;
    }

    @computed
    private get canReturnFromReview() {
        return this.importStore.usageStatus !== 'loading';
    }

    @computed
    get isReadyToImport() {
        return this.resolutionStatus === 'ready' && this.importStore.affectedTestProjects.length === 0;
    }

    @computed
    get canResolveTestProjectWithBindings() {
        const project = this.testProjectReviewContext?.project;

        if (project?.inputsType !== 'Tags') {
            return false;
        }

        return this.importStore.replacedTagsUsedInTestProjects.some(
            usage =>
                project.topics.some(topic => topic.value === usage.tagId) &&
                usage.testProjects.some(affected => affected.testProjectId === project.id)
        );
    }

    @action.bound
    reviewTestProject(testProjectId: string) {
        const attempt = this.importStore.pendingAttempt;

        if (
            !attempt ||
            !this.importStore.affectedTestProjects.some(project => project.testProjectId === testProjectId)
        ) {
            return;
        }

        const dataStore = new TestProjectDataStore(
            attempt.projectId,
            testProjectId,
            this.testProjectService,
            this.projectsStore
        );

        this.dataStore = dataStore;
        dataStore.loadTestProject();
    }

    @action.bound
    openInputMapping() {
        const resolution = this.testProjectReviewContext;

        if (!resolution || !this.canResolveTestProjectWithBindings) {
            return false;
        }

        const { attempt, dataStore } = resolution;

        // Returning to import or reviewing another project invalidates this editor source, even within the same attempt.
        const source = {
            dataStore,
            isCurrent: () => this.importStore.pendingAttempt === attempt && this.dataStore === dataStore,
            canEdit: () => this.testProjectReviewContext?.dataStore === dataStore
        };

        return this.editorStore.openMapping(source, 'Bindings');
    }

    @computed
    get resolutionStatus(): 'loading' | 'error' | 'ready' {
        const usageStatus = this.importStore.usageStatus;
        const dataStore = this.dataStore;

        if (usageStatus === 'error') {
            return 'error';
        }

        if (usageStatus !== 'ready') {
            return 'loading';
        }

        if (!dataStore) {
            return 'ready';
        }

        if (dataStore.projectStatus === 'error' || dataStore.inputRefreshStatus === 'error') {
            return 'error';
        }

        if (!dataStore.canUseProject || dataStore.inputRefreshStatus === 'loading') {
            return 'loading';
        }

        return 'ready';
    }

    @action.bound
    async retryResolutionStatus() {
        const attempt = this.importStore.pendingAttempt;

        if (!attempt) {
            return;
        }

        const dataStore = this.dataStore;

        const reads: Promise<boolean | void>[] = [this.importStore.refreshBlockers(attempt)];

        if (dataStore) {
            if (dataStore.inputsChange) {
                reads.push(dataStore.retryInputRefresh());
            } else {
                reads.push(dataStore.loadTestProject());
            }
        }

        await Promise.all(reads);
    }

    private get testProjectReviewContext(): TestProjectReviewContext | undefined {
        const attempt = this.importStore.pendingAttempt;
        const dataStore = this.dataStore;
        const project = dataStore?.testProject;

        if (!attempt || !dataStore || !project || this.resolutionStatus !== 'ready') {
            return undefined;
        }

        return { attempt, dataStore, project };
    }
}
